// @path: /src/pages/shelbula/workspaces/CreateWorkspaceModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader, PlusCircle, Github, Search, GitBranch, Star, Lock, AlertTriangle } from 'lucide-react';
import { authManager } from '@/managers/AuthManager';
import { apiManager } from './apiManager';
import { workspaceManager } from '@/managers/workspaceManager';
import { canAccessWorkspaces } from '@/utils/workspaceAccess';
import { buildImportedWorkspaceName, doesWorkspaceNameConflict, sanitizeWorkspaceName } from '@/utils/workspaceNameUtils';
import { isWorkspaceBackendEnabled } from '@/utils/setProdOrDev';

const CLONE_REQUEST_TIMEOUT_MS = 15 * 60 * 1000;

const withTimeout = (promise, timeoutMs, timeoutMessage) => {
    let timeoutId = null;
    const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
            reject(new Error(timeoutMessage));
        }, timeoutMs);
    });

    return Promise.race([promise, timeoutPromise]).finally(() => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    });
};

/* ========================
 * TabButton - A button component for modal tabs
 * ========================*/
const TabButton = ({ isActive, onClick, children }) => (
    <button
        onClick={onClick}
        className={`flex-1 px-4 py-2 text-sm font-semibold rounded-md flex items-center justify-center gap-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary
            ${isActive
                ? 'bg-primary text-white shadow-md'
                : 'text-on-surface-dim hover:bg-surface-dark'
            }`}
    >
        {children}
    </button>
);

const RepoListItem = ({ repo, onSelect }) => (
    <button
        onClick={() => onSelect(repo)}
        className="group flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors rounded-lg hover:bg-surface-dark"
    >
        <Github size={18} className="text-on-surface-dim flex-shrink-0 group-hover:text-primary transition-colors" />
        <div className="flex-1 overflow-hidden">
            <div className="flex items-center gap-2">
                <p className="truncate text-sm font-medium text-primary">{repo.full_name}</p>
                {repo.private && <Lock size={14} className="text-on-surface-dim flex-shrink-0" title="Private Repository" />}
            </div>
            <div className="mt-1 flex items-center gap-4 text-xs text-on-surface-dim">
                {repo.language && <span className="flex items-center gap-1.5">{repo.language}</span>}
                <span className="flex items-center gap-1.5"><Star size={12} />{repo.stargazers_count}</span>
                <span className="flex items-center gap-1.5"><GitBranch size={12} />{repo.default_branch}</span>
            </div>
        </div>
    </button>
);


const CreateWorkspaceModal = ({ isOpen, onClose, isGithubPatReady, setActiveWorkspaceId, onWorkspaceCreated, showNotification }) => {
    const [isMounted, setIsMounted] = useState(false);
    const [activeTab, setActiveTab] = useState('create');

    const [workspaceName, setWorkspaceName] = useState('');
    const [repoUrl, setRepoUrl] = useState('');
    const [repos, setRepos] = useState([]);
    const [isLoadingRepos, setIsLoadingRepos] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRepoListVisible, setIsRepoListVisible] = useState(false);

    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [authState] = useState(authManager.getState());
    const canUseWorkspaces = canAccessWorkspaces(authState.user, isWorkspaceBackendEnabled());

    useEffect(() => {
        setIsMounted(true);
        if (isOpen) {
            setActiveTab('create');
            setWorkspaceName('');
            setRepoUrl('');
            setError(null);
            setIsProcessing(false);
            setIsRepoListVisible(false);
            setSearchTerm('');
            setRepos([]);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    useEffect(() => {
        if (isOpen && activeTab === 'clone' && isGithubPatReady && authState.user && isRepoListVisible && repos.length === 0) {
            const fetchRepos = async () => {
                setIsLoadingRepos(true);
                setError(null);
                try {
                    const data = await apiManager.listMyGithubRepos();
                    setRepos(data.repos || []);
                } catch (err) {
                    setError(`Failed to load repositories: ${err.message}`);
                    setRepos([]);
                } finally {
                    setIsLoadingRepos(false);
                }
            };
            fetchRepos();
        }
    }, [isOpen, activeTab, isGithubPatReady, authState.user, isRepoListVisible, repos.length]);

    const filteredRepos = useMemo(() => {
        if (!searchTerm) return repos;
        return repos.filter(repo => repo.full_name.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [repos, searchTerm]);

    const derivedCloneWorkspaceName = useMemo(() => buildImportedWorkspaceName(repoUrl), [repoUrl]);

    const handleSelectRepo = (repo) => {
        setRepoUrl(repo.clone_url);
        setIsRepoListVisible(false);
    };

    const handleCreate = async () => {
        const nextWorkspaceName = sanitizeWorkspaceName(workspaceName);
        if (!nextWorkspaceName || isProcessing || !authState.user) return;

        const existingWorkspaces = workspaceManager.getState().workspaces || [];
        if (doesWorkspaceNameConflict(existingWorkspaces, nextWorkspaceName)) {
            setError(`A workspace named '${nextWorkspaceName}' already exists.`);
            return;
        }

        setIsProcessing(true);
        setError(null);
        try {
            const result = await apiManager.createWorkspace(nextWorkspaceName);
            const newWorkspaceId = result?.workspace?.id || result?.workspaceId || null;
            onClose(); // Close modal immediately
            await workspaceManager.fetchWorkspaces();
            if (showNotification) showNotification(`Workspace '${nextWorkspaceName}' is being created.`, 'info');
            if (newWorkspaceId) {
                if (typeof onWorkspaceCreated === 'function') {
                    onWorkspaceCreated(newWorkspaceId);
                } else if (typeof setActiveWorkspaceId === 'function') {
                    setActiveWorkspaceId(newWorkspaceId);
                }
            }
        } catch (err) {
            console.error("Failed to create workspace:", err);
            setError(err.message || "An unknown error occurred.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleClone = async () => {
        const normalizedRepoUrl = repoUrl.trim();
        if (!normalizedRepoUrl || isProcessing || !authState.user) return;
        const derivedWorkspaceName = buildImportedWorkspaceName(normalizedRepoUrl);
        setIsProcessing(true);
        setError(null);
        const longRunningNoticeTimer = setTimeout(() => {
            if (showNotification) {
                showNotification('Clone is still running. Large repositories can take several minutes.', 'info');
            }
        }, 8000);
        try {
            const result = await withTimeout(
                apiManager.cloneRepository(normalizedRepoUrl, derivedWorkspaceName),
                CLONE_REQUEST_TIMEOUT_MS,
                'Clone request timed out after 15 minutes. Please retry. If it keeps failing, there may be a backend/network issue.'
            );
            const newWorkspaceId = result?.workspace?.id || result?.workspaceId || null;
            onClose(); // Close modal immediately
            await workspaceManager.fetchWorkspaces();
            if (showNotification) showNotification(`Cloning repository into workspace '${derivedWorkspaceName}'...`, 'info');
            if (newWorkspaceId) {
                if (typeof onWorkspaceCreated === 'function') {
                    onWorkspaceCreated(newWorkspaceId);
                } else if (typeof setActiveWorkspaceId === 'function') {
                    setActiveWorkspaceId(newWorkspaceId);
                }
            }
        } catch (err) {
            console.error("Failed to clone repository:", err);
            const message = err.message || "An unknown error occurred.";
            setError(message);
            if (showNotification) {
                showNotification(message, 'error');
            }
        } finally {
            clearTimeout(longRunningNoticeTimer);
            setIsProcessing(false);
        }
    };

    const handleSubmit = () => {
        if (activeTab === 'create') handleCreate();
        else handleClone();
    };

    const isSubmitDisabled = isProcessing || (activeTab === 'create' && !workspaceName.trim()) || (activeTab === 'clone' && !repoUrl.trim());

    if (!isMounted || !canUseWorkspaces) return null;

    return ReactDOM.createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[6000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-lg bg-surface rounded-xl border border-border-color shadow-xl"
                    >
                        <div className="flex justify-between items-center p-4 border-b border-border-color">
                            <h2 className="text-lg font-bold text-on-surface flex items-center gap-2.5">
                                <PlusCircle size={20} className="text-primary" />
                                Create New Workspace
                            </h2>
                            <button onClick={onClose} className="p-1 rounded-full text-on-surface-dim hover:bg-surface-dark hover:text-on-surface transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-4">
                            <div className="p-1 bg-surface-dark rounded-lg flex items-center gap-2 mb-6">
                               <TabButton isActive={activeTab === 'create'} onClick={() => setActiveTab('create')}>
                                   <PlusCircle size={16} /> Create Blank
                               </TabButton>
                               <TabButton isActive={activeTab === 'clone'} onClick={() => setActiveTab('clone')}>
                                   <Github size={16} /> Clone from GitHub
                               </TabButton>
                            </div>

                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeTab}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    {activeTab === 'create' ? (
                                        <div className="space-y-2">
                                            <label htmlFor="workspaceName" className="text-sm font-medium text-on-surface">Workspace Name</label>
                                            <p className="text-xs text-on-surface-dim -mt-1 mb-2">Give your new workspace a descriptive name. Names must be unique within your account.</p>
                                            <input
                                                id="workspaceName"
                                                type="text"
                                                value={workspaceName}
                                                onChange={(e) => setWorkspaceName(e.target.value)}
                                                placeholder="e.g., 'my-awesome-project'"
                                                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                                                className="w-full px-3 py-1.5 bg-background-dark border border-border-color rounded-md text-on-surface focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none transition-colors duration-200"
                                                autoFocus
                                            />
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                             <div>
                                                 <label htmlFor="repoUrl" className="text-sm font-medium text-on-surface">GitHub Repository URL</label>
                                                 <p className="text-xs text-on-surface-dim -mt-1 mb-2">Paste a repository URL to clone and create a new workspace. Import names get a timestamp suffix automatically.</p>
                                                 <input
                                                     id="repoUrl"
                                                     type="text"
                                                     value={repoUrl}
                                                     onChange={(e) => setRepoUrl(e.target.value)}
                                                     placeholder="e.g., 'https://github.com/user/repo.git'"
                                                     onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                                                     className="w-full px-3 py-1.5 bg-background-dark border border-border-color rounded-md text-on-surface focus:ring-1 focus:ring-primary focus:border-primary focus:outline-none transition-colors duration-200"
                                                     autoFocus
                                                 />
                                                 {repoUrl.trim() && (
                                                     <p className="mt-2 text-xs text-on-surface-dim">
                                                         Workspace name: <span className="font-mono text-primary">{derivedCloneWorkspaceName}</span>
                                                     </p>
                                                 )}
                                             </div>

                                             <div className="border-t border-border-color pt-4">
                                                <button
                                                    onClick={() => setIsRepoListVisible(prev => !prev)}
                                                    disabled={!isGithubPatReady}
                                                    title={!isGithubPatReady ? "Add a GitHub PAT to list your private repositories" : "Select from your repositories"}
                                                    className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <GitBranch size={16} />
                                                    <span>{isRepoListVisible ? 'Hide Your Repositories' : 'Select From Your Repositories'}</span>
                                                </button>
                                                
                                                {!isGithubPatReady && (
                                                    <div className="text-xs text-warning p-3 rounded-md bg-surface-dark border border-border-color flex items-center gap-2 mt-3">
                                                        <AlertTriangle size={16} />
                                                        <span>A GitHub PAT is required to list and clone private repositories. Public repos can be cloned via URL.</span>
                                                    </div>
                                                )}

                                                 <AnimatePresence>
                                                     {isRepoListVisible && isGithubPatReady && (
                                                         <motion.div
                                                             initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                                             animate={{ opacity: 1, height: 'auto', marginTop: '16px' }}
                                                             exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                                             className="overflow-hidden"
                                                         >
                                                             <div className="border border-border-color rounded-lg overflow-hidden bg-background-dark">
                                                                 <div className="relative flex items-center border-b border-border-color">
                                                                     <div className="absolute left-0 flex items-center pl-3 pointer-events-none">
                                                                         <Search size={16} className="text-on-surface-dim" />
                                                                     </div>
                                                                     <input
                                                                         type="text"
                                                                         placeholder="Search repositories..."
                                                                         value={searchTerm}
                                                                         onChange={(e) => setSearchTerm(e.target.value)}
                                                                         className="w-full pl-9 pr-3 py-1.5 bg-transparent border-0 text-on-surface focus:ring-0 focus:outline-none"
                                                                     />
                                                                 </div>
                                                                 <div className="max-h-60 overflow-y-auto custom-scrollbar">
                                                                     {isLoadingRepos ? (
                                                                         <div className="flex items-center justify-center p-8 gap-2 text-on-surface-dim">
                                                                             <Loader size={16} className="animate-spin" /> Loading...
                                                                         </div>
                                                                     ) : filteredRepos.length > 0 ? (
                                                                         filteredRepos.map(repo => <RepoListItem key={repo.id} repo={repo} onSelect={handleSelectRepo} />)
                                                                     ) : (
                                                                         <p className="text-center text-on-surface-dim text-sm p-4">No repositories found.</p>
                                                                     )}
                                                                 </div>
                                                             </div>
                                                         </motion.div>
                                                     )}
                                                 </AnimatePresence>
                                             </div>
                                        </div>
                                    )}
                                </motion.div>
                            </AnimatePresence>

                            {error && (
                                <div className="flex items-start gap-3 text-sm text-error-bubble-text bg-error-bubble border border-error-bubble-border p-3 rounded-lg mt-4">
                                    <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                                    <span>{error}</span>
                                </div>
                            )}
                        </div>
                        
                        <div className="flex justify-end items-center p-4 border-t border-border-color">
                             <button 
                                 onClick={handleSubmit} 
                                 disabled={isSubmitDisabled}
                                 className="px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white hover:bg-primary-dark transition-colors disabled:bg-surface-dark disabled:text-text-disabled disabled:cursor-not-allowed flex items-center gap-2"
                             >
                                {isProcessing && <Loader size={16} className="animate-spin" />}
                                {activeTab === 'create' ? 'Create Workspace' : 'Clone Repository'}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    , document.body);
};

export default CreateWorkspaceModal;